// Kartenablauf C1: Welche Aufgabe kommt als Nächstes? Reine Funktionen (docs/PLAN_VARIANTE_C_V1.md, Abschnitte 7–9).
// Eingaben: abgeleiteter Lernstand (deriveC1), Inhalt, Zeitpunkt, Zeitzone, Einstellungen, Ausblendungen, Sitzungsstand und Startpunkt der Einstufung.
// Einstufung (Entscheidung 34): Wörter vor dem Startpunkt (opt.startPos) gelten als vorausgesetzt und blockieren keinen Satz. Die aus dem Abschnitt
// direkt davor (ab opt.checkFrom) kommen vom Startpunkt rückwärts als echte Hörprüfung (task_role 'placement'), höchstens P.placementPerDay je
// Lerntag; frühere werden nicht kennengelernt und nicht geprüft.
// Vor jeder Karte neu aufrufen: Änderungen durch Synchronisierung oder Ausblenden wirken sofort, keine alte Warteschlange läuft blind weiter.
import { studyDay } from './learning-policy.js';

const H = 3600e3;
const RESUME_WINDOW = 12 * H;   // begonnene Aufgabe: innerhalb von 12 Stunden fortsetzen, sonst gilt sie als abgebrochen

// Zähler des Lerntags aus den Versuchen (Lerntag laut Ereignis, damit ein Zeitzonenwechsel nichts verschiebt)
export function dayCounts(D, day) {
  const c = { introduced: 0, regularDone: 0, controlsUsed: 0, interleavedUsed: 0, voluntaryUsed: 0, placementDone: 0 };
  for (const a of D.attempts) {
    const d = a.events[0] && a.events[0].study_day;
    if (d !== day) continue;
    if (a.role === 'introduction' && a.introducedAt !== null) c.introduced++;
    if (a.role === 'regular' && a.review) c.regularDone++;
    if (a.role === 'placement' && a.review) c.placementDone++;
    if (a.role === 'control' && (a.review || a.assisted || a.audioStarted)) {   // ernsthaft begonnen: Ton lief oder Hilfe; technischer Fehler ohne Hörversuch zählt nicht
      c.controlsUsed++;
      if (a.controlKind === 'voluntary') c.voluntaryUsed++; else c.interleavedUsed++;
    }
  }
  return c;
}

const lastEngageOfSentence = (D, sid) => (D.sentenceLast && D.sentenceLast[sid] !== undefined ? D.sentenceLast[sid] : null);

// Satz für eine Aufgabe: nur geprüfte Zuordnungen (c1.tasks), Voraussetzungen eingeführt oder durch die Einstufung vorausgesetzt (assumed),
// nicht ausgeblendet, beim Hören mit Ton
export function pickSentence(D, unit, skill, now, { hidden = { s: {} }, excluded = new Set(), sentences, avoidCtx = null, assumed = () => false } = {}) {
  const t = D.targets[unit.id], P = D.policy;
  const tasks = ((unit.c1 && unit.c1.tasks) || []).filter(x => x[skill]);
  const SE = sentences;
  const cands = [];
  for (const x of tasks) {
    const s = SE[x.s];
    if (!s || hidden.s[s.id] || excluded.has(s.id)) continue;
    if (skill === 'h' && !s.audio) continue;
    if (!s.units.every(u => (D.targets[u] && D.targets[u].introducedAt !== null) || assumed(u))) continue;   // kein unbekannter Zusatzinhalt als Fehler des alten Ziels
    const reviews = t ? t[skill].reviews : [];
    const used = reviews.filter(r => r.sentence === s.id), lastUsed = used.length ? used[used.length - 1].at : 0;
    const le = lastEngageOfSentence(D, s.id);
    cands.push({ s, ctx: x.ctx, fresh: le === null || now - le >= P.independent, avoid: avoidCtx !== null && x.ctx === avoidCtx, ctxUses: reviews.filter(r => r.ctx === x.ctx).length, lastUsed });
  }
  cands.sort((a, b) => (b.fresh - a.fresh) || (a.avoid - b.avoid) || (a.ctxUses - b.ctxUses) || (a.lastUsed - b.lastUsed) || (a.s.id < b.s.id ? -1 : 1));
  return cands[0] || null;
}

function seededPick(list, seedText) {   // reproduzierbare Zufallsauswahl (Konto, Lerntag, Policy)
  let h = 2166136261;
  for (let i = 0; i < seedText.length; i++) { h ^= seedText.charCodeAt(i); h = Math.imul(h, 16777619); }
  return list[(h >>> 0) % list.length];
}

// Übersicht: alles, was jetzt bereitsteht, und wann das Nächste fällig wird
export function overview(D, content, opt) {
  const { now, tz, settings = {}, hidden = { s: {}, u: {} }, session = {}, noAudio = false, userKey = '', startPos = 1, checkFrom = 1 } = opt;
  const P = D.policy, day = studyDay(now, tz), counts = dayCounts(D, day);
  const units = content.units.slice().sort((a, b) => a.pos - b.pos);
  const posOf = Object.fromEntries(units.map(u => [u.id, u.pos]));
  const assumed = id => posOf[id] < startPos;   // durch die Einstufung vorausgesetzt
  const SE = opt.sentences || Object.fromEntries(content.sentences.map(s => [s.id, s]));
  const excluded = session.excluded || new Set(), skipped = session.skippedTargets || new Set();   // in dieser Sitzung übersprungen
  const regular = [], retries = [], controls = [], findings = [];
  let nextAt = null;
  const later = at => { if (at > now && (nextAt === null || at < nextAt)) nextAt = at; };

  for (const u of units) {
    const t = D.targets[u.id];
    if (!t || t.introducedAt === null || hidden.u[u.id] || !u.c1 || skipped.has(u.id)) continue;
    const skills = u.c1.skills || 'hp';
    const lock = t.lastEngageAt === null ? 0 : t.lastEngageAt + P.offerGap;
    const offers = [];
    for (const sk of ['h', 'p']) {
      if (!skills.includes(sk)) continue;
      if (sk === 'h' && noAudio) continue;
      const S = t[sk], last = S.reviews[S.reviews.length - 1];
      if (sk === 'h' && t.mode === 'control') {   // ruhend: keine reguläre Hörfrage, höchstens eine Kontrolle
        const base = Math.max(t.controlBase || 0, t.lastHAttemptAt || 0);
        const readyAt = Math.max(S.card ? S.card.due : 0, base + P.controlMinGap, t.lastEngageAt === null ? 0 : t.lastEngageAt + P.independent);
        if (readyAt <= now) controls.push({ t, u, readyAt }); else later(readyAt);
        continue;
      }
      // Wiederlernen nach Note 1: einmal je 24 h, zum tatsächlich geplanten Zeitpunkt (heute etwa 10 Minuten), nicht vorher
      if (last && last.r === 1 && last.role !== 'retry') {
        const recent = S.reviews.filter(r => r.role === 'retry' && last.at - r.at < P.offerGap && r.at <= last.at).length;
        if (recent < P.retryPer24h) {
          const at = S.card.due;
          if (at <= now) { retries.push({ t, u, skill: sk, readyAt: at }); continue; }
          later(at);
        }
      }
      const due = S.reviews.length ? S.card.due : t.introducedAt + (sk === 'h' ? P.initialH : P.initialP);
      const readyAt = Math.max(due, lock);
      offers.push({ t, u, skill: sk, readyAt });
    }
    const ready = offers.filter(o => o.readyAt <= now);
    if (ready.length) {   // höchstens ein regulärer Abruf je Ziel: ältere Fälligkeit zuerst, Gleichstand Hören zuerst
      ready.sort((a, b) => (a.readyAt - b.readyAt) || (a.skill === 'h' ? -1 : 1));
      regular.push(ready[0]);
    } else for (const o of offers) later(o.readyAt);
  }
  regular.sort((a, b) => (a.readyAt - b.readyAt) || (a.u.pos - b.u.pos));
  retries.sort((a, b) => a.readyAt - b.readyAt);

  // Neue Ziele: Tageswert, Bremse bei mehr als 20 bereiten Aufgaben, nur mit eingeführten Voraussetzungen
  const brake = regular.length + retries.length > 20;
  const budget = brake ? 0 : Math.max(0, (settings.newPerDay ?? 8) + (opt.extraNew || 0) - counts.introduced);   // extraNew: „Noch mehr lernen“ heute
  const fresh = [];
  for (const u of units) {
    if (fresh.length >= budget) break;
    const t = D.targets[u.id];
    if ((t && t.introducedAt !== null) || hidden.u[u.id] || !u.c1 || assumed(u.id)) continue;
    const s = SE[u.main];
    if (!s || hidden.s[s.id]) continue;
    // Reihenfolge: erst die Voraussetzungen (eingeführt, vorausgesetzt oder heute schon davor in dieser Liste)
    if (!s.units.every(x => x === u.id || (D.targets[x] && D.targets[x].introducedAt !== null) || assumed(x) || fresh.some(f => f.u.id === x))) break;
    fresh.push({ u, s });
  }

  // Prüfungen aus der Einstufung: vorausgesetzte, noch nicht eingeführte Ziele ab checkFrom, vom Startpunkt rückwärts, als Hörprüfung (ohne Ton
  // heute keine); gebremst wie Neues, weil jede Prüfung künftige Wiederholungen nach sich zieht
  const placement = [];
  const placementBudget = brake || noAudio ? 0 : Math.max(0, P.placementPerDay - counts.placementDone);
  for (let i = units.length - 1; i >= 0 && placement.length < placementBudget; i--) {
    const u = units[i];
    if (!assumed(u.id) || u.pos < checkFrom) continue;
    const t = D.targets[u.id];
    if ((t && t.introducedAt !== null) || hidden.u[u.id] || !u.c1 || skipped.has(u.id) || !(u.c1.skills || 'hp').includes('h')) continue;
    placement.push({ u });
  }

  // Kontrollen: nur in c1, höchstens 2 je Lerntag, eine je 5 abgeschlossene reguläre Aufgaben; erster Platz = am längsten bereit
  controls.sort((a, b) => (a.readyAt - b.readyAt) || (a.u.id < b.u.id ? -1 : 1));
  const cap = P.reduce ? P.controlsPerDay - counts.controlsUsed : 0;
  const interleaveFree = Math.floor(counts.regularDone / P.controlEvery) > counts.interleavedUsed;
  const voluntaryFree = counts.voluntaryUsed < P.voluntaryPerDay;
  const controlPick = () => {
    if (!controls.length || cap <= 0) return null;
    if (counts.controlsUsed === 0) return controls[0];
    return seededPick(controls, `${userKey}|${day}|${P.version}|${counts.controlsUsed}`);
  };
  return { day, counts, regular, retries, controls, fresh, placement, brake, budget, cap, interleaveFree, voluntaryFree, controlPick, nextAt, findings, SE, excluded, assumed };
}

// Nächste Aufgabe. session: { regularSinceNew, voluntary (Kurzer Hörcheck gewünscht), excluded }
export function nextTask(D, content, opt) {
  const { now, hidden = { s: {}, u: {} }, session = {} } = opt;
  const O = overview(D, content, opt);
  const units = Object.fromEntries(content.units.map(u => [u.id, u]));
  const mk = (kind, role, u, pick, extra = {}) => pick ? { kind, role, target: u.id, rev: (u.c1 && u.c1.rev) || 1, sentence: pick.s.id, ctx: pick.ctx, ...extra } : null;
  const finding = (u, skill) => O.findings.push(`${u.id} (${u.text}): kein geeigneter Satz für ${skill === 'h' ? 'Hören' : 'Selbst sagen'}`);

  // 1. Begonnene Aufgabe fortsetzen (gleiche attempt_id, ihre Hilfen bleiben bestehen)
  for (const t of Object.values(D.targets)) {
    const a = t.openAttempt, u = units[t.id];
    if (!a || !u || hidden.u[u.id] || hidden.s[a.sentence] || (O.excluded && O.excluded.has(a.sentence)) || (session.skippedTargets && session.skippedTargets.has(u.id))) continue;
    if (now - (a.presentedAt ?? 0) > RESUME_WINDOW) continue;
    if (a.skill === 'h' && opt.noAudio) continue;
    const kind = a.role === 'introduction' ? 'intro' : a.skill;
    return { task: { kind, role: a.role, target: t.id, rev: a.rev, sentence: a.sentence, ctx: a.ctx, attemptId: a.id, resume: true, controlKind: a.controlKind || undefined }, O };
  }
  // 2. Wiederlernen nach Note 1
  for (const r of O.retries) {
    const p = pickSentence(D, r.u, r.skill, now, { hidden, excluded: O.excluded, sentences: O.SE, assumed: O.assumed });
    if (p) return { task: mk(r.skill, 'retry', r.u, p), O };
    finding(r.u, r.skill);
  }
  // 3. Eingestreute Hörkontrolle
  if (O.interleaveFree && O.cap > 0) {
    const c = O.controlPick();
    if (c) {
      const p = pickSentence(D, c.u, 'h', now, { hidden, excluded: O.excluded, sentences: O.SE, avoidCtx: c.t.lastSuccessCtx, assumed: O.assumed });
      if (p) return { task: mk('h', 'control', c.u, p, { controlKind: 'interleaved' }), O };
      finding(c.u, 'h');
    }
  }
  // 4. Reguläre Aufgaben und neue Ziele gleichmäßig gemischt
  const regs = [];
  for (const r of O.regular) {
    const p = pickSentence(D, r.u, r.skill, now, { hidden, excluded: O.excluded, sentences: O.SE, assumed: O.assumed });
    if (p) regs.push(mk(r.skill, 'regular', r.u, p)); else finding(r.u, r.skill);
  }
  for (const r of O.placement) {   // nach den fälligen Aufgaben, im selben Wechsel mit Neuem
    const p = pickSentence(D, r.u, 'h', now, { hidden, excluded: O.excluded, sentences: O.SE, assumed: O.assumed });
    if (p) regs.push(mk('h', 'placement', r.u, p)); else finding(r.u, 'h');
  }
  const fresh = O.fresh.map(f => ({ kind: 'intro', role: 'introduction', target: f.u.id, rev: (f.u.c1 && f.u.c1.rev) || 1, sentence: f.s.id, ctx: null }));
  const every = fresh.length ? Math.max(1, Math.floor(regs.length / fresh.length)) : Infinity;
  if (fresh.length && (!regs.length || (session.regularSinceNew || 0) >= every)) return { task: fresh[0], O };
  if (regs.length) return { task: regs[0], O };
  // 5. Nichts Reguläres mehr: freiwilliger kurzer Hörcheck (einmal je Lerntag, im selben Budget)
  if (session.voluntary && O.voluntaryFree && O.cap > 0) {
    const c = O.controlPick();
    if (c) {
      const p = pickSentence(D, c.u, 'h', now, { hidden, excluded: O.excluded, sentences: O.SE, avoidCtx: c.t.lastSuccessCtx, assumed: O.assumed });
      if (p) return { task: mk('h', 'control', c.u, p, { controlKind: 'voluntary' }), O };
    }
  }
  return { task: null, O };
}
